import React, { useState, useEffect, useCallback } from 'react';
import { 
  RefreshCw, 
  Settings, 
  Download,
  Wifi,
  WifiOff,
  Clock,
  Home
} from 'lucide-react';
import { AlertsBar } from './AlertsBar';
import { SpendEfficiencyTable } from './SpendEfficiencyTable';
import { SmartRecommendations } from './SmartRecommendations';
import PageHeader from '../PageHeader';
import {
  DashboardState,
  DashboardAlert,
  SpendEfficiencyMetric,
  DashboardRecommendation,
  QuickAction,
  ActionResult
} from '../../../shared/types/dashboard';

interface DailyDecisionDashboardProps {
  userId?: string;
  autoRefresh?: boolean;
  refreshInterval?: number; // milliseconds
}

export const DailyDecisionDashboard: React.FC<DailyDecisionDashboardProps> = ({
  userId,
  autoRefresh = true,
  refreshInterval = 60000 // 1 minute default
}) => {
  const [state, setState] = useState<DashboardState>({
    alerts: [],
    campaigns: [],
    recommendations: [],
    isLoading: true,
    isRefreshing: false,
    loadingSection: null,
    lastSync: null,
    syncStatus: 'offline',
    selectedCampaign: null,
    sortColumn: null,
    sortDirection: 'asc',
    expandedAlert: null,
    expandedRecommendation: null,
    alertFilters: {
      severity: ['critical', 'warning', 'opportunity'],
      types: ['budget', 'performance', 'milestone', 'system', 'delivery'],
      showAcknowledged: false
    },
    efficiencyFilters: {
      timeframe: '24h',
      showOnlyActive: true,
      minSpend: 0,
      healthFilter: 'all'
    },
    recommendationFilters: {
      types: ['optimize', 'pause', 'scale', 'launch', 'reallocate'],
      minConfidence: 60,
      maxRecommendations: 3
    }
  });

  // Mock data for demonstration - replace with actual API calls
  const mockAlerts: DashboardAlert[] = [
    {
      id: 'alert-1',
      severity: 'critical',
      type: 'delivery',
      title: "Campaign 'Summer Tour' stopped delivering",
      description: 'Campaign has not delivered any impressions in the last 3 hours',
      campaign: { id: 'camp-1', name: 'Summer Tour' },
      metric: 0,
      threshold: 100,
      action: {
        label: 'Fix Now',
        type: 'fix',
        params: { campaign_id: 'camp-1' }
      },
      timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000)
    },
    {
      id: 'alert-2',
      severity: 'warning',
      type: 'budget',
      title: "Budget depleting in 2 days for 'New Single'",
      description: 'At current spend rate, budget will be exhausted soon',
      campaign: { id: 'camp-2', name: 'New Single' },
      metric: 85,
      threshold: 100,
      action: {
        label: 'Extend Budget',
        type: 'extend',
        params: { campaign_id: 'camp-2', amount: 500 }
      },
      timestamp: new Date(Date.now() - 30 * 60 * 1000)
    },
    {
      id: 'alert-3',
      severity: 'opportunity',
      type: 'milestone',
      title: "Artist 'The Waves' hit 10K streams milestone",
      description: 'Consider boosting campaign to capitalize on momentum',
      artist: { id: 'artist-1', name: 'The Waves' },
      metric: 10000,
      threshold: 10000,
      action: {
        label: 'Boost Campaign',
        type: 'boost',
        params: { artist_id: 'artist-1' }
      },
      timestamp: new Date(Date.now() - 15 * 60 * 1000)
    }
  ];

  const mockCampaigns: SpendEfficiencyMetric[] = [
    {
      campaign_id: 'camp-1',
      campaign_name: 'Summer Tour',
      artist_name: 'The Waves',
      status: 'active',
      today_spend: 127.43,
      yesterday_spend: 115.20,
      streams_generated: 3421,
      cost_per_stream: 0.037,
      trend: {
        direction: 'up',
        percentage: 12
      },
      health_score: 'good',
      sparkline_data: [2800, 2900, 3100, 3000, 3200, 3350, 3421],
      meta_campaign_id: 'meta-123',
      last_updated: new Date()
    },
    {
      campaign_id: 'camp-2',
      campaign_name: 'New Single',
      artist_name: 'Sarah Chen',
      status: 'active',
      today_spend: 89.21,
      yesterday_spend: 92.50,
      streams_generated: 1892,
      cost_per_stream: 0.047,
      trend: {
        direction: 'stable',
        percentage: 0
      },
      health_score: 'average',
      sparkline_data: [1750, 1800, 1850, 1900, 1880, 1890, 1892],
      meta_campaign_id: 'meta-456',
      last_updated: new Date()
    },
    {
      campaign_id: 'camp-3',
      campaign_name: 'Acoustic EP',
      artist_name: 'Nick Kandler',
      status: 'active',
      today_spend: 45.00,
      yesterday_spend: 38.75,
      streams_generated: 2100,
      cost_per_stream: 0.021,
      trend: {
        direction: 'up',
        percentage: 34
      },
      health_score: 'good',
      sparkline_data: [1500, 1600, 1700, 1850, 1950, 2000, 2100],
      meta_campaign_id: 'meta-789',
      last_updated: new Date()
    },
    {
      campaign_id: 'camp-4',
      campaign_name: 'Rock Anthem',
      artist_name: 'Thunder Road',
      status: 'active',
      today_spend: 75.50,
      yesterday_spend: 78.00,
      streams_generated: 892,
      cost_per_stream: 0.085,
      trend: {
        direction: 'down',
        percentage: -15
      },
      health_score: 'poor',
      sparkline_data: [1200, 1150, 1100, 1050, 980, 920, 892],
      meta_campaign_id: 'meta-101',
      last_updated: new Date()
    }
  ];

  const mockRecommendations: DashboardRecommendation[] = [
    {
      id: 'rec-1',
      priority: 1,
      type: 'scale',
      title: "Increase budget on 'Acoustic EP'",
      reasoning: "Converting 3x better than portfolio average with strong upward trend",
      impact: {
        metric: 'streams',
        value: 500,
        unit: 'absolute',
        timeframe: 'daily',
        display: '+500 streams/day'
      },
      confidence: 92,
      action: {
        label: 'Boost by $50/day',
        type: 'budget_change',
        params: {
          campaign_id: 'camp-3',
          adjustment_type: 'budget',
          adjustment_value: 50
        }
      },
      details: 'This campaign shows exceptional performance with a cost per stream of $0.021, well below the portfolio average of $0.048. The 7-day trend shows consistent growth (+34%), and the audience engagement metrics are strong. Increasing budget now could capitalize on this momentum.',
      data_sources: ['meta_ads', 'spotify', 'historical'],
      created_at: new Date()
    },
    {
      id: 'rec-2',
      priority: 1,
      type: 'pause',
      title: "Pause 'Rock Anthem' Facebook ads",
      reasoning: "0 conversions in 48 hours, CTR below 0.5%",
      impact: {
        metric: 'spend',
        value: 75,
        unit: 'absolute',
        timeframe: 'daily',
        display: 'Save $75/day'
      },
      confidence: 88,
      action: {
        label: 'Pause Now',
        type: 'pause_campaign',
        params: {
          campaign_id: 'camp-4'
        }
      },
      details: 'This campaign is significantly underperforming with a cost per stream of $0.085, nearly double the portfolio average. The declining trend (-15%) and poor engagement metrics suggest audience fatigue or targeting issues.',
      data_sources: ['meta_ads', 'spotify'],
      created_at: new Date()
    },
    {
      id: 'rec-3',
      priority: 2,
      type: 'launch',
      title: "Launch Instagram campaign for 'Sarah Chen'",
      reasoning: "Spotify followers grew 40% this week, indicating momentum",
      impact: {
        metric: 'streams',
        value: 2000,
        unit: 'absolute',
        timeframe: 'weekly',
        display: 'Est. 2,000 streams at $0.025/stream'
      },
      confidence: 76,
      action: {
        label: 'Create Campaign',
        type: 'create_campaign',
        params: {
          artist_id: 'artist-2',
          platform: 'instagram',
          budget: 100
        }
      },
      details: 'Sarah Chen shows strong organic growth on Spotify with a 40% increase in followers this week. Historical data suggests Instagram campaigns for similar artists in this growth phase yield excellent ROI.',
      data_sources: ['spotify', 'historical', 'ai_analysis'],
      created_at: new Date()
    }
  ];

  // Load dashboard data
  const loadDashboardData = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, isLoading: true }));
      
      // Simulate API call - replace with actual API calls
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setState(prev => ({
        ...prev,
        alerts: mockAlerts,
        campaigns: mockCampaigns,
        recommendations: mockRecommendations,
        isLoading: false,
        lastSync: new Date(),
        syncStatus: 'connected'
      }));
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        syncStatus: 'error'
      }));
    }
  }, []);

  // Refresh dashboard data
  const refreshDashboard = useCallback(async () => {
    setState(prev => ({ ...prev, isRefreshing: true }));
    await loadDashboardData();
    setState(prev => ({ ...prev, isRefreshing: false }));
  }, [loadDashboardData]);

  // Handle alert action
  const handleAlertAction = async (alert: DashboardAlert): Promise<void> => {
    console.log('Executing alert action:', alert);
    // Implement actual action logic here
    // This would call your API to fix delivery issues, extend budgets, etc.
    
    // Remove alert after action
    setState(prev => ({
      ...prev,
      alerts: prev.alerts.filter(a => a.id !== alert.id)
    }));
  };

  // Handle quick action from efficiency table
  const handleQuickAction = async (campaignId: string, action: QuickAction): Promise<void> => {
    console.log('Executing quick action:', campaignId, action);
    // Implement actual action logic here
    // This would call your API to pause/resume campaigns, adjust budgets, etc.
  };

  // Handle recommendation execution
  const handleRecommendation = async (recommendation: DashboardRecommendation): Promise<void> => {
    console.log('Executing recommendation:', recommendation);
    // Implement actual recommendation logic here
    // This would call your API to execute the recommended action
  };

  // Handle alert dismissal
  const handleAlertDismiss = (alertId: string) => {
    setState(prev => ({
      ...prev,
      alerts: prev.alerts.filter(a => a.id !== alertId)
    }));
  };

  // Handle campaign click
  const handleCampaignClick = (campaignId: string) => {
    console.log('Opening campaign details:', campaignId);
    // Navigate to campaign details page or open modal
  };

  // Initial load
  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  // Auto-refresh
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      refreshDashboard();
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, refreshDashboard]);

  const formatLastSync = (date: Date | null) => {
    if (!date) return 'Never';
    
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    
    if (minutes < 1) return 'Just now';
    if (minutes === 1) return '1 minute ago';
    if (minutes < 60) return `${minutes} minutes ago`;
    
    const hours = Math.floor(minutes / 60);
    if (hours === 1) return '1 hour ago';
    return `${hours} hours ago`;
  };

  return (
    <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-full">
      <div className="max-w-7xl mx-auto space-y-6">
        <PageHeader
          icon={<Home className="h-8 w-8" />}
          title="Dashboard"
          actions={
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2">
                {state.syncStatus === 'connected' ? (
                  <Wifi className="h-4 w-4 text-green-500" />
                ) : (
                  <WifiOff className="h-4 w-4 text-red-500" />
                )}
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {state.syncStatus === 'connected' ? 'Live' : 'Offline'}
                </span>
                {state.lastSync && (
                  <>
                    <span className="text-gray-300 dark:text-gray-600">•</span>
                    <Clock className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {formatLastSync(state.lastSync)}
                    </span>
                  </>
                )}
              </div>
              <button
                onClick={refreshDashboard}
                disabled={state.isRefreshing}
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
              >
                <RefreshCw className={`h-5 w-5 ${state.isRefreshing ? 'animate-spin' : ''}`} />
              </button>
              <button className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
                <Settings className="h-5 w-5" />
              </button>
              <button className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
                <Download className="h-5 w-5" />
              </button>
            </div>
          }
        />
        {/* Alerts Bar */}
        <AlertsBar
          alerts={state.alerts}
          onAction={handleAlertAction}
          onDismiss={handleAlertDismiss}
        />

        {/* Spend Efficiency Table */}
        <SpendEfficiencyTable
          campaigns={state.campaigns}
          onQuickAction={handleQuickAction}
          onCampaignClick={handleCampaignClick}
          isLoading={state.isLoading}
        />

        {/* Smart Recommendations */}
        <SmartRecommendations
          recommendations={state.recommendations}
          onExecute={handleRecommendation}
          isLoading={state.isLoading}
        />
      </div>
    </div>
  );
};
